import React, { useState, useEffect } from 'react';
import { Grid, Typography, Button, TableContainer, Table, TableHead, TableRow, TableCell, Paper, TableBody } from '@mui/material';
import { BusinessRule } from '../../utils/BusinessRulesEngine';
import { sortByActionSortOrder } from '../../Data/BusinessRulesData';

export interface ActionEditorProps {
  action: string;
  businessRules?: BusinessRule[];
  onSave: (ids: number[]) => void;
  onCancel: () => void;
  canEdit: boolean;
}

export const ActionEditor: React.FC<ActionEditorProps> = props => {
  const [assigned, setAssigned] = useState<BusinessRule[]>([]);
  const [available, setAvailable] = useState<BusinessRule[]>([]);
  const [draggedItem, setDraggedItem] = useState<BusinessRule | null>(null);

  useEffect(() => {
    const all = props.businessRules || [];
    setAssigned(sortByActionSortOrder(all, props.action));
    setAvailable(all.filter(x => !x.businessRuleActions?.find(y => y.action === props.action && !y.deleted)));
  }, [props.businessRules]);

  const handleDragStart = (item: BusinessRule) => {
    setDraggedItem(item);
  };

  const handleDrop = (listSetter: React.Dispatch<React.SetStateAction<BusinessRule[]>>, list: BusinessRule[], index: number | null) => {
    if (!draggedItem) return;

    // Remove the item from its current list
    // Remove the dragged item from its current list
    if (assigned.some((x) => x.id === draggedItem.id)) {
      setAssigned((prev) => prev.filter((x) => x.id !== draggedItem.id));
    } else if (available.some((x) => x.id === draggedItem.id)) {
      setAvailable((prev) => prev.filter((x) => x.id !== draggedItem.id));
    }

    // Add the item to the target list at the specified index
    listSetter(prev => {
      let newList = [...prev];
      newList = newList.filter(x => x.id !== draggedItem.id); // Ensure no duplicates
      if (index === null) {
        newList.push(draggedItem);
      } else {
        newList.splice(index, 0, draggedItem);
      }
      return newList;
    });

    setDraggedItem(null);
  };

  return (
    <div style={{ padding: 24, width: '100%' }}>
      <Typography variant="h4">Action: {props.action}</Typography>
      <hr />

      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Typography variant="h6">Assigned Rules</Typography>
          <ActionList
            sortedBusinessRules={assigned}
            onDragStart={handleDragStart}
            onDrop={(index) => handleDrop(setAssigned, assigned, index)}
            canEdit={props.canEdit}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography variant="h6">Available Rules</Typography>
          <ActionList
            sortedBusinessRules={available}
            onDragStart={handleDragStart}
            onDrop={(index) => handleDrop(setAvailable, available, index)}
            canEdit={props.canEdit}
          />
        </Grid>
      </Grid>

      <div style={{ margin: 24 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            const ids = assigned?.map(x => x.id);
            props.onSave(ids);
          }}
          disabled={!props.canEdit}
        >
          Save
        </Button>
        <Button
          variant="contained"
          color="inherit"
          style={{ marginLeft: 10 }}
          onClick={() => props.onCancel()}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

interface ActionListProps {
  sortedBusinessRules: BusinessRule[];
  onDragStart: (item: BusinessRule) => void;
  onDrop: (index: number | null) => void;
  canEdit?: boolean;
}

const ActionList: React.FC<ActionListProps> = (props) => {
  const handleDragOver = (e: React.DragEvent<HTMLTableRowElement | HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLTableRowElement | HTMLDivElement>, index: number | null) => {
    e.preventDefault();
    props.onDrop(index);
  };

  return (
    <TableContainer component={Paper} onDragOver={handleDragOver} >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>Description</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Active</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.sortedBusinessRules.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} style={{ textAlign: 'center' }}>
                No rules available
              </TableCell>
            </TableRow>
          )}
          {props.sortedBusinessRules.map((br, index) => (
            <TableRow
              key={br.id}
              draggable={props.canEdit}
              onDragStart={() => props.onDragStart(br)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              style={{ cursor: props.canEdit ? 'grab' : 'default' }}
            >
              <TableCell>{br.description}</TableCell>
              <TableCell>{br.type}</TableCell>
              <TableCell>{!br.deleted ? 'Yes' : 'No'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};